import { Column, ColumnConfig } from '@ant-design/charts';
import { useLazyQuery } from '@apollo/client';
import {
  GET_IDEA_COUNT_BY_DAYS,
  IdeaCountByDays,
  IdeaCountByDaysChartData,
} from '@frontend/data-access';
import { Card } from 'antd';
import { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

function IdeaCountsByDays({
  channelId,
  challengeId,
  innovationSpaceId,
}: {
  channelId?: string;
  challengeId?: string;
  innovationSpaceId?: string;
}) {
  const { t } = useTranslation('common');
  const [data, setData] = useState<IdeaCountByDaysChartData[]>([]);

  const [getIdeaCountByDays] = useLazyQuery<{
    ideaCountByPhaseAndDays: IdeaCountByDays[];
  }>(GET_IDEA_COUNT_BY_DAYS, {
    onCompleted(data) {
      const newData: IdeaCountByDaysChartData[] = [];
      // Add everything into a list
      for (const item of data.ideaCountByPhaseAndDays) {
        for (const stageCount of item.stageCount) {
          newData.push({
            days: t('duration_days', {
              count: item.days?.replace(' days', '') || '',
            }),
            items: stageCount.items,
            phase: stageCount.phase,
          });
        }
      }
      setData(newData);
    },
  });

  function sortByDays(data: IdeaCountByDaysChartData[]) {
    const parseDayRange = (range: string) => {
      if (range.startsWith('>')) {
        return Number(range.slice(1, range.indexOf(' '))) + 1e6; // large number to push it last
      }
      const match = range.match(/^(\d+)-/);
      return match ? parseInt(match[1], 10) : 0;
    };

    return data.sort((a, b) => parseDayRange(a.days) - parseDayRange(b.days));
  }

  useEffect(() => {
    if (!channelId) return;
    getIdeaCountByDays({
      variables: { channelId, challengeId, spaceId: innovationSpaceId },
    });
  }, [channelId, challengeId, innovationSpaceId, getIdeaCountByDays]);

  const config: ColumnConfig = {
    data: sortByDays(data),
    isGroup: true,
    xField: 'days',
    yField: 'items',
    seriesField: 'phase',
    colorField: 'phase',
    // Colors matching the image
    scale: {
      color: {
        range: ['#FACC14', '#63E063', '#529FF0', '#CACACA'],
      },
    },
    columnStyle: {
      radius: [4, 4, 0, 0],
    },
    label: {
      position: 'top',
      dy: -15,
      style: {
        fill: '#000000',
        fontSize: 12,
      },
      formatter: (value: number) => {
        if (value === 0) {
          return '';
        }
        return value.toString();
      },
    },
    axis: {
      x: {
        label: {
          autoRotate: false,
        },
      },
    },
    legend: {
      color: {
        position: 'bottom',
        layout: {
          justifyContent: 'center',
          alignItems: 'center',
        },
      },
    },
  };

  return (
    <Card style={{ borderRadius: 20 }}>
      <div className="space-y-4">
        <div className="text-sm text-gray-900 font-medium">
          {t('ideas_days')}
        </div>
        <Column {...config} height={400} />
      </div>
    </Card>
  );
}

export default IdeaCountsByDays;
